import React, {useState} from "react";
import {Row, Col, Button, Select, DatePicker, Tag, message, Tooltip, Popover} from "antd";
import {
    BellOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    InfoCircleOutlined
} from "@ant-design/icons";
import moment from "moment";
import {useStore} from "../../../../hooks/useStore";
import {useFetch} from "../../../../hooks/useFetch";
import {NotificationContentModal} from "./NotificationContentModal";
import {TestNotificationPopup} from "./components/TestNotificationPopup";
import {TableContainer} from "./CourierNotificationsTab.styles";
import NotificationTypeFilter from "components/_defaultComponents/FilterComponents/NotificationTypeFilter";
import {DELIVERY_STATUS_ITEMS, oneSignalNotificationTypes} from "constants/constants";
import {VFlex} from "components/_defaultComponents/Flex/VFlex";
import {VText} from "components/_defaultComponents/Text/VText";
import {Shortener} from "components/_defaultComponents/Shortener/Shortener";
import JSONInput from "react-json-editor-ajrm";

const {RangePicker} = DatePicker;

export const CourierNotificationsTab = ({courierId}) => {
    const stores = useStore();
    const [notifications, setNotifications] = useState([]);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0
    });
    const [filters, setFilters] = useState({
        status: "all",
        dateRange: null,
        notificationType: null
    });
    const [contentModalVisible, setContentModalVisible] = useState(false);
    const [selectedNotification, setSelectedNotification] = useState(null);

    const [loading, notificationsData, , , refetch] = useFetch({
        defaultDataValue: null,
        fetchFn: () => {
            const params = {
                page: 1,
                limit: 20,
                status: filters.status || "all"
            };

            if (filters.dateRange && filters.dateRange.length === 2) {
                params.startDate = filters.dateRange[0].toISOString();
                params.endDate = filters.dateRange[1].toISOString();
            }

            if (filters.notificationType) {
                params.notificationType = filters.notificationType;
            }

            return stores.courierStore.getCourierNotifications(courierId, params);
        },
        dependencies: [
            courierId,
            filters.status,
            filters.dateRange?.[0]?.toISOString(),
            filters.dateRange?.[1]?.toISOString(),
            filters.notificationType
        ],
        condition: Boolean(courierId),
        callbackFunc: result => {
            if (result?.success) {
                const fetchedNotifications = result?.data?.notifications || [];
                setNotifications(fetchedNotifications);
                setPagination(prev => ({
                    ...prev,
                    total: fetchedNotifications.length
                }));
            } else {
                message.error("Bildirimler yüklenirken bir hata oluştu");
            }
        }
    });

    const handleSendTestNotification = async () => {
        try {
            const response = await stores.courierStore.sendTestNotification(courierId);
            if (response.success) {
                message.success("Test bildirimi başarıyla gönderildi");
            } else {
                message.error(response.message || "Test bildirimi gönderilemedi");
            }
            // Başarılı veya hatalı olsa da tabloyu yenile
            refetch();
        } catch (error) {}
    };

    const handleContentClick = record => {
        setSelectedNotification(record);
        setContentModalVisible(true);
    };

    const getNotificationTitle = payload => {
        try {
            if (typeof payload === "string") {
                payload = JSON.parse(payload);
            }
            const title = payload?.data?.title;
            if (title) {
                return title;
            }
            return "-";
        } catch (e) {
            return "-";
        }
    };
    const getNotificationContent = payload => {
        try {
            if (typeof payload === "string") {
                payload = JSON.parse(payload);
            }
            const contents = payload?.data?.contents;
            if (contents && contents.tr) {
                return contents.tr;
            }
            const contentTr = payload?.data?.contentTr;
            if (contentTr) {
                return contentTr;
            }
            return "-";
        } catch (e) {
            return "-";
        }
    };

    const columns = [
        {
            title: "Gönderim Zamanı",
            dataIndex: "createdAt",
            render: date => (
                <span data-label="Gönderim Zamanı:">
                    {moment(date).format("DD/MM/YYYY HH:mm:ss")}
                </span>
            ),
            sorter: (a, b) => moment(a.createDate).diff(moment(b.createDate))
        },
        {
            title: "Gönderim Durumu",
            dataIndex: "isSuccess",
            render: isSuccess => (
                <span data-label="Gönderim Durumu:">
                    {isSuccess ? (
                        <Tag icon={<CheckCircleOutlined />} color="success">
                            Başarılı
                        </Tag>
                    ) : (
                        <Tag icon={<CloseCircleOutlined />} color="error">
                            Başarısız
                        </Tag>
                    )}
                </span>
            )
        },
        {
            title: "Görülme Durumu",
            dataIndex: "isSeen",
            render: (_, notification) => (
                <div data-label="Görülme Durumu:">
                    {notification.isSeen ? (
                        <Tooltip
                            title={
                                <>
                                    {notification.seenAt
                                        ? moment(notification.seenAt).format("DD/MM/YYYY HH:mm:ss")
                                        : "-"}
                                    {notification.seenAppState
                                        ? ` (${notification.seenAppState})`
                                        : ""}
                                </>
                            }
                        >
                            <Tag icon={<CheckCircleOutlined />} color="success">
                                Görüldü
                            </Tag>
                        </Tooltip>
                    ) : (
                        "-"
                    )}
                </div>
            )
        },
        {
            title: "Listede Görüldü",
            dataIndex: "isSeenOnList",
            render: (_, notification) => (
                <div data-label="Listede Görüldü:">
                    {notification.isSeenOnList ? (
                        <Tooltip
                            title={
                                notification.seenOnListAt
                                    ? moment(notification.seenOnListAt).format(
                                          "DD/MM/YYYY HH:mm:ss"
                                      )
                                    : "-"
                            }
                        >
                            <Tag icon={<CheckCircleOutlined />} color="success">
                                Görüldü
                            </Tag>
                        </Tooltip>
                    ) : (
                        "-"
                    )}
                </div>
            )
        },
        {
            title: "Tıklanma Durumu",
            dataIndex: "isTapped",
            render: (_, notification) => (
                <div data-label="Tıklanma Durumu:">
                    {notification.isTapped ? (
                        <>
                            <Tooltip
                                title={
                                    notification.tappedAt
                                        ? moment(notification.tappedAt).format(
                                              "DD/MM/YYYY HH:mm:ss"
                                          )
                                        : "-"
                                }
                            >
                                <Tag icon={<CheckCircleOutlined />} color="success">
                                    Tıklandı
                                </Tag>
                            </Tooltip>
                        </>
                    ) : (
                        "-"
                    )}
                </div>
            )
        },
        {
            title: "Bildirim Türü",
            dataIndex: "payload",
            render: payload => (
                <span data-label="Bildirim Türü:">
                    {
                        oneSignalNotificationTypes.find(type => type.value === payload.collapseId)
                            ?.label
                    }
                </span>
            )
        },
        {
            title: "Bildirim İçeriği",
            dataIndex: "payload",
            render: (payload, record) => (
                <Tag>
                    <b>
                        <Shortener text={getNotificationTitle(payload)} length={50} />
                    </b>
                    <br />
                    <Shortener text={getNotificationContent(payload)} length={50} />
                </Tag>
            )
        },
        {
            title: "Detaylı Bilgi",
            dataIndex: "payload",
            align: "right",
            width: 80,
            render: (_, notification) => (
                <Popover
                    content={
                        <JSONInput
                            id="notificationJSON"
                            placeholder={notification}
                            theme="dark_mitsuketa_tribute"
                            width="auto"
                            height="auto"
                            viewOnly={true}
                            confirmGood={false}
                        />
                    }
                >
                    <InfoCircleOutlined />
                </Popover>
            )
        }
    ];

    return (
        <div>
            <Row gutter={[16, 16]} style={{marginBottom: "20px"}}>
                <Col>
                    <h5>Gönderim Durumu</h5>
                    <Select
                        style={{width: 150}}
                        value={filters.status}
                        onChange={status => {
                            setFilters({...filters, status});
                            setPagination({...pagination, current: 1});
                        }}
                        allowClear
                    >
                        <Select.Option value="all">Tümü</Select.Option>
                        <Select.Option value="success">Başarılı</Select.Option>
                        <Select.Option value="failed">Başarısız</Select.Option>
                    </Select>
                </Col>
                <Col>
                    <h5>Gönderim Zamanı</h5>
                    <RangePicker
                        showTime={{format: "HH:mm"}}
                        format="DD/MM/YYYY HH:mm"
                        placeholder={["Başlangıç", "Bitiş"]}
                        value={filters.dateRange}
                        onChange={dateRange => {
                            setFilters({...filters, dateRange});
                            setPagination({...pagination, current: 1});
                        }}
                    />
                </Col>
                <Col>
                    <h5>Bildirim Tipi</h5>
                    <NotificationTypeFilter
                        style={{width: 250}}
                        value={filters.notificationType}
                        onChange={notificationType => {
                            setFilters({...filters, notificationType});
                            setPagination({...pagination, current: 1});
                        }}
                    />
                </Col>
                <Col
                    flex="auto"
                    style={{
                        textAlign: "right",
                        display: "flex",
                        alignItems: "flex-end",
                        justifyContent: "flex-end"
                    }}
                >
                    <TestNotificationPopup onConfirm={handleSendTestNotification}>
                        <Button type="primary" icon={<BellOutlined />}>
                            Test Bildirimi Gönder
                        </Button>
                    </TestNotificationPopup>
                </Col>
            </Row>

            <TableContainer
                size="small"
                loading={loading}
                columns={columns}
                dataSource={notifications}
                rowKey={record => record._id}
                pagination={{
                    ...pagination,
                    onChange: page => setPagination({...pagination, current: page}),
                    showSizeChanger: false
                }}
            />
            <NotificationContentModal
                visible={contentModalVisible}
                onClose={() => setContentModalVisible(false)}
                notification={selectedNotification}
            />
        </div>
    );
};
